import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import guardianViewingsService from '../services/guardianViewingsService';
import styles from '../assets/guardianViewings.module.css';

const SortIcon = ({ direction, isActive }) => (
  <svg
    width="10"
    height="16"
    viewBox="0 0 10 16"
    style={{ marginLeft: 4, verticalAlign: "middle" }}
  >
    <g fill={isActive ? "#333" : "#bbb"}>
      {direction === "asc" ? (
        <polygon points="5,3 8,7 2,7" />
      ) : direction === "desc" ? (
        <polygon points="5,13 2,9 8,9" />
      ) : (
        <>
          <polygon points="5,3 8,7 2,7" />
          <polygon points="5,13 2,9 8,9" />
        </>
      )}
    </g>
  </svg>
);

const GuardianViewings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [viewingStatus, setViewingStatus] = useState(null);
  const [viewingSchedule, setViewingSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  // ✅ Laravel-compatible status IDs
  const [selectedStatus, setSelectedStatus] = useState('999'); // All (Laravel: 999)
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [recordsPerPage, setRecordsPerPage] = useState(30);

  // State for viewing schedule table sorting
  const [sortField, setSortField] = useState("day");
  const [sortDirection, setSortDirection] = useState("asc");

  // Handle viewing schedule sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Sort viewing schedule for display
  const sortedViewingSchedule = [...viewingSchedule].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];

    // Handle nested object sorting - map sortField to actual data field names
    if (sortField === "property") {
      aValue = a.name || "";
      bValue = b.name || "";
    } else if (sortField === "postcode") {
      aValue = a.postcode || "";
      bValue = b.postcode || "";
    } else if (sortField === "day") {
      aValue = a.day || "";
      bValue = b.day || "";
    } else if (sortField === "from") {
      aValue = a.time_from || "";
      bValue = b.time_from || "";
    } else if (sortField === "to") {
      aValue = a.time_to || "";
      bValue = b.time_to || "";
    } else if (sortField === "status") {
      aValue = a.status || "";
      bValue = b.status || "";
    }

    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  useEffect(() => {
    fetchViewingStatus();
    fetchViewingSchedule();
  }, []);

  useEffect(() => {
    // Add event listeners for action buttons after component mounts
    const handleActionClick = (e) => {
      if (e.target.classList.contains('cancel-viewing')) {
        const viewingId = e.target.getAttribute('data-viewing-id');
        handleCancelViewing(viewingId);
      } else if (e.target.classList.contains('confirm-viewing')) {
        const viewingId = e.target.getAttribute('data-viewing-id');
        handleConfirmViewing(viewingId);
      }
    };

    document.addEventListener('click', handleActionClick);

    return () => {
      document.removeEventListener('click', handleActionClick);
    };
  }, []);

  useEffect(() => {
    fetchViewingSchedule();
  }, [selectedStatus, currentPage, recordsPerPage, searchTerm]);

  // ✅ Enhanced error handling for viewing status
  const fetchViewingStatus = async () => {
    try {
      console.log('🔍 [FRONTEND] Fetching viewing status...');
      
      const response = await guardianViewingsService.getViewingStatus();
      
      if (response.success) {
        console.log('✅ [FRONTEND] Viewing status loaded:', response.data);
        setViewingStatus(response.data);
        
        // Log data source for debugging
        if (response.source === 'fallback') {
          console.warn('⚠️ [FRONTEND] Using fallback viewing status data');
        }
      } else {
        console.error('❌ [FRONTEND] Failed to get viewing status:', response.message);
        setViewingStatus({
          all: 0, upcoming: 0, past: 0, going: 0, attended: 0, notAttended: 0
        });
        // Error handled silently
      }
    } catch (error) {
      console.error('❌ [FRONTEND] Error fetching viewing status:', error);
      
      // Set default values on error
      setViewingStatus({
        all: 0, upcoming: 0, past: 0, going: 0, attended: 0, notAttended: 0
      });
      
      // Error handled silently - no toast shown
    }
  };

  // ✅ Enhanced error handling for viewing schedule
  const fetchViewingSchedule = async () => {
    try {
      console.log('🔍 [FRONTEND] Fetching viewing schedule...', { selectedStatus, currentPage, recordsPerPage, searchTerm });
      
      const params = {
        status_id: selectedStatus,
        draw: currentPage,
        start: (currentPage - 1) * recordsPerPage,
        length: recordsPerPage,
        search: { value: searchTerm },
      };

      const response = await guardianViewingsService.getViewingSchedule(params);
      
      if (response.data) {
        console.log(`✅ [FRONTEND] Viewing schedule loaded: ${response.data.length} viewings`);
        setViewingSchedule(response.data);
        setTotalRecords(response.recordsTotal || 0);
        
        // Log data source for debugging
        if (response.source === 'fallback') {
          console.warn('⚠️ [FRONTEND] Using fallback viewing schedule data');
        }
      } else {
        console.error('❌ [FRONTEND] No data in viewing schedule response');
        setViewingSchedule([]);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error('❌ [FRONTEND] Error fetching viewing schedule:', error);
      
      // Set default values on error
      setViewingSchedule([]);
      setTotalRecords(0);
      
      if (error.response?.status === 403) {
        toast.error("You don't have permission to view viewing schedule");
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to load viewing schedule');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStatusClick = (statusId) => {
    setSelectedStatus(statusId);
    setCurrentPage(1);
  };

  // ✅ Laravel-compatible cancel viewing with enhanced error handling
  const handleCancelViewing = async (viewingId) => {
    try {
      console.log('🔍 [FRONTEND] Cancelling viewing:', viewingId);
      
      const result = await guardianViewingsService.cancelViewing(viewingId);
      
      if (result.success) {
        console.log('✅ [FRONTEND] Viewing cancelled successfully');
        toast.success(result.message || 'Your confirmation has been deleted!'); // Laravel message
        
        // Refresh both status and schedule
        fetchViewingStatus();
        fetchViewingSchedule();
      } else {
        console.error('❌ [FRONTEND] Cancel failed:', result.message);
        toast.error(result.message || 'Failed to cancel viewing');
      }
    } catch (error) {
      console.error('❌ [FRONTEND] Error cancelling viewing:', error);
      
      // Handle specific error responses
      if (error.response?.status === 403) {
        toast.error("You don't have permission to cancel this viewing");
      } else if (error.response?.status === 404) {
        toast.error("There is no confirmation to delete!");
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to cancel viewing. Please try again.');
      }
    }
  };

  // ✅ Laravel-compatible confirm viewing with enhanced error handling  
  const handleConfirmViewing = async (viewingId) => {
    try {
      console.log('🔍 [FRONTEND] Confirming viewing status:', viewingId);
      
      const result = await guardianViewingsService.confirmViewing(viewingId, 'going'); // Laravel status name
      
      if (result.success) {
        console.log('✅ [FRONTEND] Viewing status updated successfully');
        const statusMessage = result.data ? 
          `Status changed from ${result.data.oldStatus?.name} to ${result.data.newStatus?.name}` : 
          'Viewing confirmed successfully';
        
        toast.success(result.message || statusMessage);
        
        // Refresh both status and schedule
        fetchViewingStatus();
        fetchViewingSchedule();
      } else {
        console.error('❌ [FRONTEND] Confirm failed:', result.message);
        toast.error(result.message || 'Failed to confirm viewing');
      }
    } catch (error) {
      console.error('❌ [FRONTEND] Error confirming viewing:', error);
      
      // Handle specific error responses
      if (error.response?.status === 403) {
        toast.error("You don't have permission to change viewing status");
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.message || "Invalid viewing status");
      } else if (error.response?.status === 404) {
        toast.error("Viewing confirmation not found");
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to confirm viewing. Please try again.');
      }
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleRecordsPerPageChange = (e) => {
    setRecordsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className={styles.guardianViewings}>
        <div className={styles.container}>
          <div style={{ textAlign: 'center', padding: '50px' }}>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.guardianViewings}>
      <div className={styles.container}>
        {/* Breadcrumbs */}
        {/* <nav className={styles.breadcrumb}>
          <ol>
            <li>
              <span onClick={() => navigate('/guardian-dashboard')} style={{ cursor: 'pointer', color: '#337ab7' }}>Dashboard</span>
            </li>
            <li className={styles.active}>My Viewings</li>
          </ol>
        </nav> */}


        <div className={styles.breadcrumbs}>
          <span 
            className={styles.breadcrumbLink}
            onClick={() => navigate('/guardian-dashboard')}
          >
            Dashboard
          </span>
          <span className={styles.breadcrumbSeparator}>/</span>
          <span className={styles.currentPage}>My Viewings</span>
        </div>


        {/* Viewing Status Section */}
        <div className={styles.panel}>
          <div className={styles.panelHeading}>
            <h3>Viewing Status</h3>
          </div>
          <div className={styles.panelBody}>
            <p>These boxes help you track your property viewings:</p>
            <ul>
              <li>All: View all scheduled viewings.</li>
              <li>Upcoming: See future scheduled viewings.</li>
              <li>Past: Review viewings that have already taken place.</li>
              <li>Going: Confirmed viewings you plan to attend.</li>
              <li>Attended: Viewings you have attended.</li>
              <li>Not Attended: Viewings you missed or did not attend.</li>
            </ul>
          </div>
        </div>

        {/* Status Overview Boxes - ✅ Laravel-compatible status IDs */}
        <div className={styles.statusOverview}>
          <div
            className={styles.statusBox}
            onClick={() => handleStatusClick('999')}
            style={{ backgroundColor: '#5bc0de' }}
          >
            <p className={styles.statusValue}>{viewingStatus?.all || 0}</p>
            <p className={styles.statusTitle}>All</p>
          </div>

          <div
            className={styles.statusBox}
            onClick={() => handleStatusClick('100')}
            style={{ backgroundColor: '#5cb85c' }}
          >
            <p className={styles.statusValue}>{viewingStatus?.upcoming || 0}</p>
            <p className={styles.statusTitle}>Upcoming</p>
          </div>

          <div
            className={styles.statusBox}
            onClick={() => handleStatusClick('101')}
            style={{ backgroundColor: '#d9534f' }}
          >
            <p className={styles.statusValue}>{viewingStatus?.past || 0}</p>
            <p className={styles.statusTitle}>Past</p>
          </div>

          <div
            className={styles.statusBox}
            onClick={() => handleStatusClick('0')}
            style={{ backgroundColor: 'midnightblue' }}
          >
            <p className={styles.statusValue}>{viewingStatus?.going || 0}</p>
            <p className={styles.statusTitle}>Going</p>
          </div>

          <div
            className={styles.statusBox}
            onClick={() => handleStatusClick('1')}
            style={{ backgroundColor: '#cc6600' }}
          >
            <p className={styles.statusValue}>{viewingStatus?.attended || 0}</p>
            <p className={styles.statusTitle}>Attended</p>
          </div>

          <div
            className={styles.statusBox}
            onClick={() => handleStatusClick('2')}
            style={{ backgroundColor: 'red' }}
          >
            <p className={styles.statusValue}>
              {viewingStatus?.notAttended || 0}
            </p>
            <p className={styles.statusTitle}>Not Attended</p>
          </div>
        </div>

        {/* Viewing Schedule Section */}
        <div className={styles.panel}>
          <div className={styles.panelHeading}>
            <h3>Viewing Schedule</h3>
          </div>
          <div className={styles.panelBody}>
            <p>This page provides a detailed list of your property viewings:</p>
            <ul>
              <li>
                Property Details: Includes the postcode, date, and time of each
                viewing.
              </li>
              <li>
                Status: Indicates whether you are "Going," have "Attended," or
                "Not Attended" the viewing.
              </li>
              <li>
                Cancel Button: Allows you to cancel a scheduled viewing if
                necessary.
              </li>
            </ul>
            <p>
              Use this page to manage your viewing appointments and keep track
              of your property visits.
            </p>

            {viewingSchedule.length > 0 ? (
              <>
                {/* Table Controls */}
                <div className={styles.tableControls}>
                  <div className={styles.showEntries}>
                    <label>
                      Show{' '}
                      <select
                        value={recordsPerPage}
                        onChange={handleRecordsPerPageChange}
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={30}>30</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>{' '}
                      entries
                    </label>
                  </div>
                  <div className={styles.searchBox}>
                    <label>Search:</label>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={handleSearch}
                      placeholder="Search properties..."
                    />
                  </div>
                </div>

                {/* Viewing Schedule Table */}
                <div className={styles.tableWrapper}>
                  <table className={styles.viewingTable}>
                    <thead>
                      <tr>
                        <th
                          className="sortable"
                          onClick={() => handleSort("property")}
                          style={{ cursor: "pointer" }}
                          title="Click to view property details"
                        >
                          Property<SortIcon direction={sortField === "property" ? sortDirection : null} isActive={sortField === "property"} />
                        </th>
                        <th 
                          className="sortable"
                          onClick={() => handleSort("postcode")}
                          style={{ cursor: "pointer" }}
                        >
                          Postcode<SortIcon direction={sortField === "postcode" ? sortDirection : null} isActive={sortField === "postcode"} />
                        </th>
                        <th 
                          className="sortable"
                          onClick={() => handleSort("day")}
                          style={{ cursor: "pointer" }}
                        >
                          Day<SortIcon direction={sortField === "day" ? sortDirection : null} isActive={sortField === "day"} />
                        </th>
                        <th 
                          className="sortable"
                          onClick={() => handleSort("from")}
                          style={{ cursor: "pointer" }}
                        >
                          From<SortIcon direction={sortField === "from" ? sortDirection : null} isActive={sortField === "from"} />
                        </th>
                        <th 
                          className="sortable"
                          onClick={() => handleSort("to")}
                          style={{ cursor: "pointer" }}
                        >
                          To<SortIcon direction={sortField === "to" ? sortDirection : null} isActive={sortField === "to"} />
                        </th>
                        <th 
                          className="sortable"
                          onClick={() => handleSort("status")}
                          style={{ cursor: "pointer" }}
                        >
                          Status<SortIcon direction={sortField === "status" ? sortDirection : null} isActive={sortField === "status"} />
                        </th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedViewingSchedule.map((viewing) => (
                        <tr key={viewing.id}>
                          <td
                            dangerouslySetInnerHTML={{ __html: viewing.name }}
                          />
                          <td>{viewing.postcode}</td>
                          <td>{viewing.day}</td>
                          <td>{viewing.time_from}</td>
                          <td>{viewing.time_to}</td>
                          <td>{viewing.status}</td>
                          <td>
                            {viewing.actions && (
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: viewing.actions,
                                }}
                              />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className={styles.pagination}>
                  <div className={styles.paginationInfo}>
                    Showing {(currentPage - 1) * recordsPerPage + 1} to{' '}
                    {Math.min(currentPage * recordsPerPage, totalRecords)} of{' '}
                    {totalRecords} entries
                  </div>
                  <div className={styles.paginationControls}>
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={styles.paginationButton}
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage * recordsPerPage >= totalRecords}
                      className={styles.paginationButton}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className={styles.noData}>
                You don't have any viewings scheduled!
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuardianViewings;
